import { useMemo } from 'react';

import path from 'path';

import { useTranslation } from 'react-i18next';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';

import LastOnline from '~components/data/lastOnline';
import ErrorPage from '~components/errorBoundary/errorPage';
import ErrorType from '~components/errorBoundary/errorType';
import { pagePathSegments, queryParameters } from '~constants';
import { UserRoleEnum } from '~enums';
import {
	DeviceDetailsContainer,
	DeviceScheduledActionsContainer,
	DeviceTelemetryContainer,
	DeviceTypeEnum,
} from '~features/devices';
import useAuthorize from '~hooks/useAuthorize';
import { extendDevice } from '~interfaces/device';
import { deviceModelImageNames } from '~interfaces/deviceModel';
import PageLayout from '~layouts/page/pageLayout';
import { getService } from '~services/serviceFactory';

import DeviceOfflineAccessContainer from '../containers/deviceOfflineAccesContainer';
import DeviceContext from '../providers/deviceContext';
import DevicesService from '../services/devicesService';

interface Props {
	tab: 'general' | 'telemetry' | 'offline-access' | 'scheduled-actions';
}

const DeviceDetailsPage = ({ tab }: Props) => {
	const { t } = useTranslation();
	const auth = useAuthorize();
	const { id: deviceId } = useParams();
	const [searchParams] = useSearchParams();
	const organisationId = searchParams.get(queryParameters.OverwrittenClientId) ?? undefined;
	const isSuperAdmin = auth.authorize([UserRoleEnum.SuperAdmin]);
	const devicesService = getService(DevicesService);

	// Load device
	const {
		data: device,
		error: deviceError,
		isLoading,
	} = useSWR(devicesService.getDeviceByIdCacheKey(deviceId, organisationId), ([_, id, orgId]) =>
		id ? devicesService.getDeviceById(id, orgId ?? undefined) : undefined
	);

	const isDoorLock = useMemo(() => {
		if (device?.model?.deviceType === DeviceTypeEnum.DoorLock) {
			return true;
		}

		return false;
	}, [device?.model?.deviceType]);

	const supportsScheduledActions = isDoorLock;
	const deviceDataExtended = extendDevice(device);
	const isWallReader = deviceDataExtended?.isWallReader ?? false;
	const showOfflineAccessTab = isDoorLock && isSuperAdmin && !isWallReader;

	const tabs = useMemo(
		() =>
			deviceId ?
				[
					{
						key: pagePathSegments.General,
						title: t('device.fields.generalInformation', { ns: 'entities' }),
						path: path.join('/', pagePathSegments.Devices, deviceId, pagePathSegments.General),
						component: <DeviceDetailsContainer />,
					},
					...(showOfflineAccessTab ?
						[
							{
								key: pagePathSegments.OfflineAccess,
								title: t('offlineAccess', { ns: 'nav' }),
								path: path.join(
									'/',
									pagePathSegments.Devices,
									deviceId,
									pagePathSegments.OfflineAccess
								),
								component: <DeviceOfflineAccessContainer />,
							},
						]
					:	[]),
					{
						key: pagePathSegments.Telemetry,
						title: t('telemetry', { ns: 'system' }),
						path: path.join('/', pagePathSegments.Devices, deviceId, pagePathSegments.Telemetry),
						component: <DeviceTelemetryContainer />,
					},
					...(supportsScheduledActions ?
						[
							{
								key: pagePathSegments.ScheduledActions,
								title: t('scheduledAction.labels.plural', { ns: 'entities' }),
								path: path.join(
									'/',
									pagePathSegments.Devices,
									deviceId,
									pagePathSegments.ScheduledActions
								),
								component: <DeviceScheduledActionsContainer />,
							},
						]
					:	[]),
				]
			:	[],
		[deviceId, organisationId, showOfflineAccessTab, supportsScheduledActions, t]
	);

	const crumbs = useMemo(
		() => [
			{
				title: t('device.labels.plural', { ns: 'entities' }),
				path: path.join('/', pagePathSegments.Devices),
			},
			{
				title: device?.name ?? deviceId ?? '',
				path: '',
			},
		],
		[device, deviceId, t]
	);

	const computedTitle = useMemo(
		() => device?.name ?? `${t('device.labels.single', { ns: 'entities' })} ${deviceId ?? ''}`,
		[device, deviceId, t]
	);

	// --- All hooks above, all early returns below ---

	if (deviceId == undefined) {
		return <Navigate to={pagePathSegments.Devices} replace />;
	}

	if (!isLoading && deviceError) {
		return <ErrorPage type={ErrorType.NotFound} />;
	}

	if (!isLoading && tab === pagePathSegments.ScheduledActions && !isDoorLock) {
		const serializedSearch = searchParams.toString();
		return (
			<Navigate
				to={{
					pathname: path.join('/', pagePathSegments.Devices, deviceId, pagePathSegments.General),
					search: serializedSearch.length > 0 ? `?${serializedSearch}` : '',
				}}
				replace
			/>
		);
	}

	if (!isLoading && tab === pagePathSegments.OfflineAccess && !showOfflineAccessTab) {
		const serializedSearch = searchParams.toString();
		return (
			<Navigate
				to={{
					pathname: path.join('/', pagePathSegments.Devices, deviceId, pagePathSegments.General),
					search: serializedSearch.length > 0 ? `?${serializedSearch}` : '',
				}}
				replace
			/>
		);
	}

	return (
		<DeviceContext value={{ device: device, overwrittenClientId: organisationId }}>
			<PageLayout
				isLoading={isLoading}
				pageTitle={computedTitle}
				preservedTabSearchParams={[queryParameters.OverwrittenClientId]}
				headerInfo={{
					title: computedTitle,
					subHeaderComponent: <LastOnline date={device?.state?.lastReport} />,
					imageName: deviceModelImageNames[device?.model?.name ?? ''],
				}}
				activeTabKey={tab}
				tabs={tabs}
				crumbs={crumbs}
			/>
		</DeviceContext>
	);
};

export default DeviceDetailsPage;
